/**
 * A Resource Plan's NOS Demand (src/api/plan-nos-demand.ts): the Account's Demands, the
 * link preview and link, unlink, and Post to NOS. Every write invalidates every plan read
 * (the grid's locked months and Plan History both move) and owns its own toast, per
 * .claude/rules/frontend.md. Previews are never cached: NOS moves underneath them.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { notAddedMessage } from "@/lib/plan-nos-demand";
import { planNosDemandKeys, resourcePlanKeys } from "@/lib/query";
import type {
  PlanNosDemandLinkPreview,
  PlanNosDemandOptions,
  PlanNosGuidancePostPreview,
  PlanNosGuidancePostResult,
} from "@/schemas/plan-nos-demand";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";
import { send, useInvalidatePlans } from "./use-resource-plans";

const base = (planId: string) => `/api/plans/${planId}/nos-demand`;

export function usePlanNosDemandOptions(planId: string, enabled: boolean) {
  return useQuery({
    queryKey: planNosDemandKeys.options(planId),
    queryFn: () => send<PlanNosDemandOptions>(`${base(planId)}/options`, "GET"),
    enabled,
    // Back from Demand Planning's Create demand page, the new Demand is in the list.
    refetchOnWindowFocus: "always",
  });
}

/** What linking `demandId` would do — read fresh from NOS each time the dialog asks. */
export function useNosDemandLinkPreview(planId: string, demandId: string) {
  return useQuery({
    queryKey: planNosDemandKeys.linkPreview(planId, demandId),
    queryFn: () => send<PlanNosDemandLinkPreview>(`${base(planId)}/preview`, "POST", { demandId }),
    enabled: demandId !== "",
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useLinkNosDemand(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (input: { demandId: string; previewToken: string; refresh: boolean }) =>
      send<ResourcePlanDetailView>(base(planId), "PUT", {
        demandId: input.demandId,
        previewToken: input.previewToken,
      }),
    onSuccess: (view, input) => {
      invalidate();
      toast.success(
        input.refresh
          ? "Closed months refreshed from NOS"
          : `Linked to ${view.nosDemand?.demandName ?? "the NOS Demand"}`,
      );
    },
    onError: (err: Error) => toast.error(err.message ?? "Couldn’t link the NOS Demand"),
  });
}

export function useUnlinkNosDemand(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: () => send<ResourcePlanDetailView>(base(planId), "DELETE"),
    onSuccess: () => {
      invalidate();
      toast.success("NOS Demand unlinked. Nothing in NOS was changed.");
    },
    onError: (err: Error) => toast.error(err.message ?? "Couldn’t unlink the NOS Demand"),
  });
}

export function useNosGuidancePostPreview(planId: string, enabled: boolean) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: planNosDemandKeys.postPreview(planId),
    queryFn: async () => {
      const preview = await send<PlanNosGuidancePostPreview>(`${base(planId)}/post-preview`, "GET");
      // NOS already had everything, so the plan's "Edits to post" just cleared.
      // Only the plan itself: every plan read would refetch this preview too.
      if (preview.recordedInSync) {
        qc.invalidateQueries({ queryKey: resourcePlanKeys.detail(planId) });
      }
      return preview;
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function usePostNosGuidance(planId: string) {
  const invalidate = useInvalidatePlans();
  return useMutation({
    mutationFn: (previewToken: string) =>
      send<PlanNosGuidancePostResult>(`${base(planId)}/post`, "POST", { previewToken }),
    onSuccess: (result) => {
      invalidate();
      const notAdded = notAddedMessage(result.peopleNotAdded, result.peopleInactiveOnDemand);
      if (notAdded !== null) toast.warning(notAdded);
      const added = `${result.resourcesAdded} Resource${result.resourcesAdded === 1 ? "" : "s"}`;
      switch (result.outcome) {
        case "posted":
          toast.success(
            `Posted to NOS: ${result.rowsWritten} month${result.rowsWritten === 1 ? "" : "s"} of guidance${
              result.resourcesAdded > 0 ? `, ${added} added` : ""
            }`,
          );
          return;
        case "nothingWritten":
          toast.error(
            "NOS took none of these hours. The months may have closed since the preview. Refresh from NOS and preview again.",
          );
          return;
        case "peopleOnly":
          toast.warning(
            `Added ${added} to the Demand in NOS, but NOS took none of their hours. The months may have closed. Refresh from NOS, then post again.`,
          );
          return;
        case "partial":
          toast.warning(
            `Posted ${result.rowsWritten} of ${result.rowsRequested} months of guidance. NOS skipped the rest; refresh from NOS and preview again.`,
          );
          return;
      }
    },
    onError: (err: Error) => toast.error(err.message ?? "Couldn’t post to NOS"),
  });
}
